import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dragAndDropArea", "imagePreview", "fileInput", "removeImageInput", "removeImageContainer"]
  static values = {
    maximumFileSize: Number,
    minimumFileSize: Number,
    maximumPixels: Number,
    supportedTypes: Array,
    errorPopupTitle: String,
    errorPopupPrompt: String,
    tooLargeErrorMessage: String,
    tooSmallErrorMessage: String,
    tooManyPixelsErrorMessage: String,
    undecodableErrorMessage: String,
    notSupportedTypeErrorMessage: String,
    unrepresentableFilePlaceholderImageUrl: String
  }

  validationRun = 0

  connect() {
    if (this.imagePresent) {
      this.previewFiles()
    }
    else {
      this.removeFiles(null)
    }
  }

  disconnect() {
    this.validationRun++
    this.imagePreviewFileReader = null
  }

  previewFiles() {
    if (!this.hasFileInputTarget) return
    if (!this.hasImagePreviewTarget) return

    const files = this.fileInputTarget.files

    if (this.hasRemoveImageInputTarget) this.removeImageInputTarget.checked = false

    this.previewFile(files[0])
  }

  previewFile(file) {
    if (!file) return

    if (file.type.startsWith("image/")) {
      this.previewImage(file)
    }
    else {
      this.previewGenericFile(file)
    }
  }

  previewImage(file) {
    const imagePreviewTarget = this.imagePreviewTarget

    this.imagePreviewFileReader = this.imagePreviewFileReader || new FileReader()

    this.imagePreviewFileReader.addEventListener("load", function () {
      imagePreviewTarget.src = this.result
    })

    this.imagePreviewFileReader.readAsDataURL(file)
  }

  previewGenericFile(_file) {
    if (!this.hasUnrepresentableFilePlaceholderImageUrlValue) return

    this.imagePreviewTarget.src = this.unrepresentableFilePlaceholderImageUrlValue
  }

  clearImagePreview() {
    if (!this.hasImagePreviewTarget) return

    this.imagePreviewTarget.removeAttribute("src")
  }

  assignFiles(event) {
    event.preventDefault()
    event.stopPropagation()

    this.dragEventCounter = 1
    this.deactivateDropArea(event)

    if (!this.hasFileInputTarget) return
    if (!event.dataTransfer) return

    const files = event.dataTransfer.files
    this.fileInputTarget.files = files
    this.announceSelection()
  }

  removeFiles(event) {
    let target = null

    if (this.hasRemoveImageInputTarget) target = this.removeImageInputTarget

    if (event && event.target && event.target.type === "checkbox") target = event.target

    if (event && this.hasFileInputTarget && this.fileInputTarget.files.length === 0 && !target.checked) {
      event.preventDefault()
      target.checked = true
    }

    const hadSelection = this.hasFileInputTarget && this.fileInputTarget.value !== ""

    this.clearSelectedFiles()
    this.clearImagePreview()
    if (this.hasRemoveImageInputTarget) this.hideElement(this.removeImageContainerTarget)

    // Dropping the file back out is just as invisible as putting it in, and a
    // file rejected by `validateFiles` leaves through here — so whoever was told
    // a photo had arrived has to be told it is gone again.
    if (hadSelection) this.announceSelection()
  }

  // Assigning `.files` in code fires nothing, so a dropped file stays invisible
  // to everything outside this controller — the Amazon Key setup gating, for
  // one — until some unrelated interaction happens to fire an event of its own.
  // Firing what the browser fires for a file picked through Upload puts both on
  // the same path: `input` is what `validateFiles` is bound to (Stimulus' default
  // event for an `input` element), and `change` is what everyone else listens for.
  announceSelection() {
    if (!this.hasFileInputTarget) return

    this.fileInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clearSelectedFiles() {
    if (!this.hasFileInputTarget) return

    this.fileInputTarget.value = ""
  }

  processDragOverDragArea(event) {
    event.preventDefault()
  }

  activateDropArea(event) {
    if (!this.hasDragAndDropAreaTarget) return

    event.preventDefault()
    this.dragEventCounter = this.dragEventCounter || 0
    this.dragEventCounter++

    if (this.dragEventCounter > 1) return
    this.dragAndDropAreaTarget.setAttribute("data-dropping")
  }

  deactivateDropArea(event) {
    if (!this.hasDragAndDropAreaTarget) return

    event.preventDefault()
    this.dragEventCounter--

    if (this.dragEventCounter > 0) return
    this.dragAndDropAreaTarget.removeAttribute("data-dropping")
  }

  async validateFiles(event) {
    if (!this.hasFileInputTarget) return

    const imageFiles = this.fileInputTarget.files

    if (imageFiles.length === 0) return
    if (this.hasRemoveImageInputTarget) this.removeImageInputTarget.checked = false

    const run = ++this.validationRun
    const errorMessages = []

    for (const file of Array.from(imageFiles)) {
      const errors = await this.validateFile(file)

      // Bail if a newer selection or a disconnect superseded us while decoding —
      // otherwise we'd clear the input and pop the error modal over whatever the
      // user is looking at now.
      if (run !== this.validationRun) return

      if (errors.length === 0) continue

      errorMessages.push(this.formatErrorMessages(file, errors))
    }

    if (errorMessages.length === 0) {
      this.fileInputTarget.dataset.pendingReselect = false
      if (this.hasRemoveImageContainerTarget) this.showElement(this.removeImageContainerTarget)
      this.previewFiles()
      return
    }

    // Set before the file is taken back out: `removeFiles` announces that, and a
    // listener acting on the empty input — the amenity policy row deletes itself
    // — has to be able to see that another file is about to be asked for.
    this.fileInputTarget.dataset.pendingReselect = true

    this.removeFiles(event)

    Rails.confirm(
      JSON.stringify(
        {
          title: this.errorPopupTitleValue || "Invalid image(s) selected",
          body: errorMessages.flat().join("\n"),
          confirm: this.errorPopupPromptValue || "Select another image"
        }
      ),
      this.fileInputTarget
    )
  }

  async validateFile(file) {
    const errors = []

    if (this.hasMaximumFileSizeValue && file.size > this.maximumFileSizeValue) {
      errors.push("tooLarge")
    }

    // Amazon rejects photos below a floor, so we catch them before upload too.
    if (this.hasMinimumFileSizeValue && file.size < this.minimumFileSizeValue) {
      errors.push("tooSmall")
    }

    const unsupportedType = this.hasSupportedTypesValue && !this.supportedTypesValue.includes(file.type)

    if (unsupportedType) {
      errors.push("notSupportedType")
    }

    // Skip the decode entirely once the file is already rejected — it's the most
    // memory-hungry step, and oversized files are exactly what reaches it.
    if (errors.length === 0 && this.hasMaximumPixelsValue && file.type.startsWith("image/")) {
      const pixels = await this.countPixels(file)

      if (pixels === null) {
        console.error(`[image-upload] could not decode ${file.name} (${file.type}, ${file.size} bytes)`)
        errors.push("undecodable")
      }
      else if (pixels > this.maximumPixelsValue) errors.push("tooManyPixels")
    }

    if (errors.length === 0) {
      if (this.hasRemoveImageContainerTarget) this.showElement(this.removeImageContainerTarget)
    }
    return errors
  }

  // Deliberately an <img>, not createImageBitmap(): browsers defer decoding for an
  // <img> that is never painted, whereas createImageBitmap() decodes eagerly — and a
  // 16 MB PNG can expand to hundreds of megapixels, which is exactly what we reject.
  countPixels(file) {
    return new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file)
      const image = new Image()

      image.addEventListener("load", () => {
        URL.revokeObjectURL(objectUrl)
        resolve(image.naturalWidth * image.naturalHeight)
      })

      image.addEventListener("error", () => {
        URL.revokeObjectURL(objectUrl)
        resolve(null)
      })

      image.src = objectUrl
    })
  }

  formatErrorMessages(file, errors) {
    const messages = []
    const controller = this

    errors.forEach((error) => {
      let message = controller[`${error}ErrorMessageValue`]

      if (message && message !== "") {
        message = message.replaceAll("%{file_name}", file.name)
        message = message.replaceAll("%{file_type}", file.type)
        message = message.replaceAll("%{maximum_megapixels}", (controller.maximumPixelsValue / 1_000_000).toLocaleString())
        message = message.replaceAll("%{minimum_file_size}", controller.humanFileSize(controller.minimumFileSizeValue))
        messages.push(message)
      }
      else {
        messages.push(`${error} ERROR`)
      }
    })

    return messages
  }

  // Matches Rails' `to_fs(:human_size)` closely enough for an error message.
  humanFileSize(bytes) {
    if (!bytes) return ""

    const units = ["Bytes", "KB", "MB", "GB"]
    let size = bytes
    let unit = 0

    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024
      unit++
    }

    return `${Number(size.toFixed(1))} ${units[unit]}`
  }

  hideElement(target) {
    target.classList.add("hidden")
  }

  showElement(target) {
    target.classList.remove("hidden")
  }

  get imagePresent() {
    if (this.hasFileInputTarget && this.fileInputTarget.files.length > 0) return true
    if (this.hasImagePreviewTarget && this.imagePreviewTarget.src) return true

    return false
  }
}
